import './styles.scss';

import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { firstNameLastInitial, getClasses, isNil, titleCase } from '@/utils';

import { Alert } from 'react-bootstrap';
import Button from '@/common/Button';
import { CompleteEvaluationWizardTypes } from '@/pages/Users/components/CompleteEvaluationWizard/types';
import { DATE_FORMAT } from '@/constants';
import { Dayts } from '@/utils/Dayts';
import { EvaluationSummaryModalTypes } from './types';
import EvaluationQuestionField from '@/pages/Users/components/EvaluationQuestionField';
import Form from '@/common/Form';
import { LoadingBlur } from '@/components/LoadingSpinner';
import { Middle } from '@/components/Align';
import Modal from '@/common/Modal';
import Textarea from '@/common/Textarea';
import { TrainingChecklistTableTypes } from '@/pages/Users/components/TrainingChecklistTable/types';
import { buildAnswersFromCategories } from '@/pages/Users/components/EvaluationQuestionField/utils';
import { createComponentState } from '@/state';
import { getEvaluation } from '@/pages/Users/api/services/getEvaluation';
import { refetchSearchUser } from '@/pages/Users/api/services/searchUsers';

type CategoryWithQuestions = TrainingChecklistTableTypes.CategoryWithQuestions;

// Open/close is derived from `attemptId`; a row double-click in DynamicReport writes it here.
export const useEvaluationSummaryModal = createComponentState<EvaluationSummaryModalTypes.State>({});

const noop = (): void => undefined;

const displayDate = (value?: string | null): string => (value ? new Dayts(value).format(DATE_FORMAT) : '—');

// "Start Date" per SKYP-9641: the date the first category was submitted.
const resolveStartDate = (categories: CategoryWithQuestions[]): string | null =>
  categories
    .map((category) => category.category.submittedAt)
    .filter(Boolean)
    .sort()[0] || null;

// Trainer of record: whoever submitted the most recently completed category, shortened to "First L.".
const resolveTrainerName = (categories: CategoryWithQuestions[]): string => {
  const submitter = [...categories]
    .filter((category) => category.category.submitterFullName)
    .sort((a, b) => (b.category.submittedAt || '').localeCompare(a.category.submittedAt || ''))[0];
  return firstNameLastInitial(submitter?.category.submitterFullName || '');
};

const categoryProgress = (category: CategoryWithQuestions): { total: number; completed: number; percent: number } => {
  const total = category.questions.length;
  const completed = category.questions.filter(({ question }) => !isNil(question.selectedAnswerOptionId)).length;
  return { total, completed, percent: total ? Math.round((completed / total) * 100) : 0 };
};

const EvaluationSummaryModal = (): ReactElement => {
  const [{ attemptId }, setStore] = useEvaluationSummaryModal(({ state, setState }) => [state, setState]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [data, setData] = useState<EvaluationSummaryModalTypes.LoadedData | undefined>();
  const requestId = useRef<number>(0);

  const closeModal = (): void => setStore({});

  const loadEvaluation = useCallback(async (id: string): Promise<void> => {
    const request = ++requestId.current;
    setLoading(true);
    setError(undefined);
    try {
      const evaluation = await getEvaluation(id);
      if (!evaluation) throw new Error(`No evaluation found for ${id}`);
      const [driver] = (await refetchSearchUser(evaluation.attempt.subjectUserId)) || [];
      if (request === requestId.current) setData({ evaluation, driver });
    } catch (err) {
      if (request === requestId.current) {
        setError(err as Error);
        setData(undefined);
      }
    } finally {
      if (request === requestId.current) setLoading(false);
    }
  }, []);

  useEffect((): void => {
    if (attemptId === undefined) {
      requestId.current += 1;
      setData(undefined);
      setError(undefined);
      return;
    }
    loadEvaluation(attemptId);
  }, [attemptId, loadEvaluation]);

  const evaluation = data?.evaluation;
  const driver = data?.driver;
  const categories = [...(evaluation?.categories || [])].sort((a, b) => a.category.sortOrder - b.category.sortOrder);
  const overallResult = evaluation?.attempt?.overallResult ?? undefined;
  const driverName = driver ? titleCase(`${driver.firstName || ''} ${driver.lastName || ''}`.trim()) : '';

  return (
    <Modal className="EvaluationSummaryModal" size="lg" title="Evaluation Summary" show={attemptId !== undefined} onHide={closeModal}>
      <LoadingBlur loading={loading || (!data && !error)} size="lg" />

      {error && (
        <div className="EvaluationSummaryModal-Error">
          <Middle.Center>
            <Alert className="EvaluationSummaryModal-ErrorAlert" variant="danger">
              <h5>
                Something went wrong.
                <small>
                  <em>{error?.message || String(error)}</em>
                </small>
              </h5>
            </Alert>
          </Middle.Center>
        </div>
      )}

      {evaluation && !error && (
        <div className="EvaluationSummaryModal-Body">
          <div className="EvaluationSummaryModal-Summary">
            <p className="EvaluationSummaryModal-Label">Evaluation Summary</p>
            <div className="EvaluationSummaryModal-Summary-Row">
              <span>
                <span className="EvaluationSummaryModal-Summary-Key">Driver:</span>
                <strong>{driverName || '—'}</strong>
              </span>
              <span>
                <span className="EvaluationSummaryModal-Summary-Key">Driver ID:</span>
                <strong>{driver?.employeeId || '—'}</strong>
              </span>
              <span>
                <span className="EvaluationSummaryModal-Summary-Key">Start Date:</span>
                <strong>{displayDate(resolveStartDate(categories))}</strong>
              </span>
              <span>
                <span className="EvaluationSummaryModal-Summary-Key">Trainer:</span>
                <strong>{resolveTrainerName(categories) || '—'}</strong>
              </span>
            </div>
            <div className="EvaluationSummaryModal-Summary-Row">
              <span>
                <span className="EvaluationSummaryModal-Summary-Key">Result:</span>
                <strong>{overallResult ? CompleteEvaluationWizardTypes.OverallResultLabel[overallResult] : '—'}</strong>
              </span>
              <span>
                <span className="EvaluationSummaryModal-Summary-Key">Type:</span>
                <strong>{driver?.role?.name ? titleCase(driver.role.name) : '—'}</strong>
              </span>
              <span>
                <span className="EvaluationSummaryModal-Summary-Key">Completion Date:</span>
                <strong>{displayDate(evaluation.attempt.submittedAt)}</strong>
              </span>
            </div>
          </div>

          <div className="EvaluationSummaryModal-Section">
            <div className="EvaluationSummaryModal-SectionHeader">
              <i className="sv sv-clipboard-check EvaluationSummaryModal-SectionIcon EvaluationSummaryModal-SectionIcon-Notes" />
              <div>
                <h5>Field Notes &amp; Evidence</h5>
                <p>Add evaluation and supporting documentation</p>
              </div>
            </div>
            <p className="EvaluationSummaryModal-Label">Field Notes</p>
            <Textarea
              className="EvaluationSummaryModal-Notes"
              disabled
              readOnly
              rows={3}
              value={
                (evaluation.notes || [])
                  .map((note) => note.note)
                  .filter(Boolean)
                  .join('\n\n') || '—'
              }
            />
            <p className="EvaluationSummaryModal-HelperText">
              These notes will be included in the driver&apos;s permanent record and may be used for performance reviews.
            </p>
          </div>

          <div className="EvaluationSummaryModal-Section">
            <p className="EvaluationSummaryModal-Label">Overall Result</p>
            <div className="EvaluationSummaryModal-ResultPills">
              {CompleteEvaluationWizardTypes.OPTIONS.map(({ value, color }) => {
                const checked = overallResult === value;
                return (
                  <Button
                    key={value}
                    type="button"
                    variant="light"
                    disabled
                    className={getClasses(
                      'EvaluationSummaryModal-ResultPill',
                      checked ? 'EvaluationSummaryModal-ResultPill-Checked' : undefined
                    )}
                    style={{ color: checked ? '#fff' : color, borderColor: color, backgroundColor: checked ? color : undefined }}
                  >
                    {CompleteEvaluationWizardTypes.OverallResultLabel[value]}
                  </Button>
                );
              })}
            </div>
            <p className="EvaluationSummaryModal-HelperText">
              {CompleteEvaluationWizardTypes.OPTIONS.find((option) => option.value === overallResult)?.description}
            </p>
          </div>

          <div className="EvaluationSummaryModal-Section">
            <div className="EvaluationSummaryModal-SectionHeader">
              <i className="sv sv-clipboard-check EvaluationSummaryModal-SectionIcon EvaluationSummaryModal-SectionIcon-Eval" />
              <h5>Driver Evaluation</h5>
            </div>
            <Form key={attemptId} value={{ answers: buildAnswersFromCategories(categories) }} onSubmit={noop}>
              {categories.map((category) => {
                const { total, completed, percent } = categoryProgress(category);
                const questions = [...category.questions].sort((a, b) => a.question.sortOrder - b.question.sortOrder);
                return (
                  <div className="EvaluationSummaryModal-Category" key={category.category.id}>
                    <div className="EvaluationSummaryModal-CategoryHeader">
                      <div>
                        <h5>{category.category.name}</h5>
                        <p className="EvaluationSummaryModal-CategoryCount">
                          {completed} of {total} Items Completed
                        </p>
                      </div>
                      <span className="EvaluationSummaryModal-CategoryPercent">{percent}%</span>
                    </div>
                    {questions.map(({ question, options }, index) => (
                      <EvaluationQuestionField key={question.id} index={index} question={question} options={options} readOnly />
                    ))}
                  </div>
                );
              })}
            </Form>
          </div>
        </div>
      )}

      {evaluation && !error && (
        <div className="EvaluationSummaryModal-Footer">
          <Button variant="outline-primary" onClick={closeModal}>
            Close
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default EvaluationSummaryModal;
